import React, { useMemo, useState, useEffect } from "react";
import "./SmokingCalendar.scss";

import { ReactComponent as TrophyIcon } from "../../../assets/img/icons/streak-trophy.svg";
import { ReactComponent as LightningBolt } from "../../../assets/img/icons/lightning-bolt.svg";
import dayjs from "dayjs";

import CustomCalendar from "../CustomCalendar";

interface SmokingCalendarProps {
    smokingLogs?: Array<{ date: string; hasSmoked: boolean }>;
}

export const SmokingCalendar: React.FC<SmokingCalendarProps> = (props: SmokingCalendarProps) => {
    const { smokingLogs } = props;
    const [dateValue, setDateValue] = useState(new Date());
    const [startDate, setStartDate] = useState<any>();

    const getTileClass = ({ date }) => {
        const formattedDate = dayjs(date).format("YYYY-MM-DD");
        const today = dayjs().format("YYYY-MM-DD");
        const earliestDate = smokingLogs?.length
            ? smokingLogs.reduce(
                (earliest, log) => (log.date < earliest.date ? log : earliest),
                smokingLogs[0]
            ).date
            : null;

        let className = "";

        if (dayjs(date).isAfter(dayjs(), "day")) return "";

        const dayLogs = smokingLogs.filter(log =>
            dayjs(log.date).isSame(formattedDate, "day")
        );

        if (dayjs(date).isSame(today, "day")) {
            if (dayLogs.length > 0) {
                const latestLog = [...dayLogs].sort((a, b) =>
                    dayjs(b.date).diff(dayjs(a.date))
                )[0];
                className = latestLog.hasSmoked ? "smoked-day" : "no-smoke-day";
                className += " current-day no-bg";
            } else {
                className += " current-day";
            }
        } else if (dayLogs.length > 0) {
            const log = dayLogs[0]; 
            className = log.hasSmoked ? "smoked-day" : "no-smoke-day";
        } else if (earliestDate && formattedDate >= earliestDate) {
            className = "no-smoke-log";
        }

        return className.trim();
    };

    useEffect(() => {
        setStartDate(new Date()); 
    }, []);


    const streakAnalysis = useMemo(() => {
        const sortedLogs = [...smokingLogs].sort((a, b) => Date.parse(a.date) - Date.parse(b.date));

        let longestStreak = 0;
        let currentStreak = 0;
        let maxStreakEnd = null;
        let currentStreakEnd = null;
        let lastSmokedDate = null;

        const isSmokingDate = dateString => {
            const log = sortedLogs.find(log => log.date === dateString);
            return !log || log.hasSmoked;
        };

        let lastDate = null;
        sortedLogs.forEach((log, index) => {
            const currentDate = new Date(log.date);

            if (log.hasSmoked) {
                lastSmokedDate = log.date;
            }

            if (lastDate) {
                let checkDate = new Date(lastDate);
                checkDate.setDate(checkDate.getDate() + 1);

                while (checkDate < currentDate) {
                    if (isSmokingDate(checkDate.toISOString().split("T")[0])) {
                        if (currentStreak > longestStreak) {
                            longestStreak = currentStreak;
                            maxStreakEnd = lastDate;
                        }
                        currentStreak = 0;
                        break;
                    }
                    currentStreak++;
                    checkDate.setDate(checkDate.getDate() + 1);
                }
            }

            if (!log.hasSmoked) {
                currentStreak++;
                currentStreakEnd = currentDate;
            } else {
                if (currentStreak > longestStreak) {
                    longestStreak = currentStreak;
                    maxStreakEnd = lastDate;
                }
                currentStreak = 0;
            }

            lastDate = currentDate;
        });

        if (currentStreak > longestStreak) {
            longestStreak = currentStreak;
            maxStreakEnd = lastDate;
        }

        return {
            longestStreak: longestStreak.toString().padStart(2, "0"),
            currentStreak: currentStreak.toString().padStart(2, "0"),
            longestStreakEnd: maxStreakEnd ? maxStreakEnd.toISOString().split("T")[0] : null,
            currentStreakEnd: currentStreakEnd ? currentStreakEnd.toISOString().split("T")[0] : null,
            lastSmokedDate: lastSmokedDate ? dayjs(lastSmokedDate).format("DD MMM YYYY") : null,
        };
    }, [smokingLogs]);

    return (
        <div className="smoking-history__body">
            <div className="smoking-history__streaks">
                <div className="smoking-history__streaks__section">
                    <div className="smoking-history__streaks__heading">Last smoked: </div>
                    <div className="smoking-history__streaks__date"> {streakAnalysis.lastSmokedDate}</div>
                </div>
                <div className="smoking-history__streaks__section">
                    <div className="smoking-history__streaks__heading">Current streak</div>
                    <div className="smoking-history__streaks__inner">
                        <LightningBolt />
                        <div>
                            <div className="smoking-history__streaks__value">{streakAnalysis.currentStreak} day/s</div>
                            <div className="smoking-history__streaks__subheading">smoke free</div>
                        </div>
                    </div>
                </div>
                <div className="smoking-history__streaks__section">
                    <div className="smoking-history__streaks__heading">Longest streak</div>
                    <div className="smoking-history__streaks__inner">
                        <TrophyIcon />
                        <div>
                            <div className="smoking-history__streaks__value">{streakAnalysis.longestStreak} day/s</div>
                            <div className="smoking-history__streaks__subheading">smoke free</div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="smoking-calendar">
                <CustomCalendar
                    dateValue={dateValue}
                    setDateValue={setDateValue}
                    calendarHeader={"Your progress so far"}
                    tileClassName={getTileClass}
                    activeStartDate={startDate}
                    onActiveStartDateChange={({ activeStartDate }) => setStartDate(activeStartDate)}
                    disableActionButtons={true}
                    checkDisabledDates={() => true}
                    error={{ present: false, type: "" }}
                    formatShortWeekday={(_, date) => ["S", "M", "T", "W", "T", "F", "S"][date.getDay()]}
                />
                <div className="smoking-calendar__legend">
                    <div className="smoking-calendar__legend__container">
                        <span className="smoking-calendar__legend__current-day" />
                        <span>Current day</span>
                    </div>
                    <div className="smoking-calendar__legend__container">
                        <span className="smoking-calendar__legend__not-smoked" />
                        <span>Not smoked</span>
                    </div>
                    <div className="smoking-calendar__legend__container">
                        <span className="smoking-calendar__legend__smoked" />
                        <span>Smoked</span>
                    </div>
                    <div className="smoking-calendar__legend__container">
                        <span className="smoking-calendar__legend__no-record" />
                        <span>No record</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
